import { useGetActiveGroupsQuery } from "@api/access-api"
import { useGetEventTypesQueryL } from "@api/eventType-api"
import { useGetGroupByIdQueryL } from "@api/group-api"
import { useGetRootGroupQueryL, useSearchRootGroupsQuery } from "@api/rootGroup-api"
import { useGetRootGroupClassesQueryL } from "@api/rootGroupClass-api"
import { useGetTaskDefinitionsQueryL } from "@api/taskDefinition-api"
import { useGetTemplateRolesQueryL } from "@api/templaterole-api"
import { userSelector } from "@redux/user"
import _ from "lodash"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { useLocation, useSearchParams } from "react-router-dom"
import { useGetVideoPlayerUrlQuery, useListGeneralVideosQuery, useListRootGroupVideosQuery } from "../api/video-api"
import { locData } from "./localization"
import { changesBetweenObjects, isWithinCustomerGroup } from "./utils"
import { getExerciseMainCategory } from "@views/Charts/utils"
import { usePlayerLikeRoleNames } from "./customization/customization"
import { formatISO, isValid, parseISO, startOfDay } from "date-fns"
import { useSendAnalyticsEventMutation } from "@api/analytics-api"

/**
 * Returns current profile's accessible groups, dependent on context.
 */
export const useGroups = () => {
	// Add non-handled query argument to refetch on context data changes
	const { contextData } = useSelector(userSelector)
	const { data } = useGetActiveGroupsQuery({ refetch: contextData })
	if (!data) {
		return []
	}

	return data.groups as any
}

/**
 * Returns current profile's accessible groups, dependent on context. Returns `useGetActiveGroupsQuery` status values unlike `useGroups`
 */
export const useGroupsWithStatus = (queryOpts: any = {}) => {
	// Add non-handled query argument to refetch on context data changes
	const { contextData } = useSelector(userSelector)
	const { data, isLoading, isFetching, isSuccess, error } = useGetActiveGroupsQuery({ refetch: contextData }, queryOpts)

	if (!data) {
		return { data: [], isLoading, isFetching, error }
	}

	return { data: data.groups as any, isLoading, isFetching, isSuccess, error }
}

/**
 * Get all shared or supervised group members; relies on activeGroup endpoint.
 * Does not include memberPool members without any assigned role.
 *
 * @param {Boolean} mergeRoles 					Members in multiple groups will have merged `hasGroupRoles` path.
 * @returns {[Object]}									Group and RootGroup members
 */
export const useAllGroupMembers = (mergeRoles: boolean = true) => {
	// Add non-handled query argument to refetch on context data changes
	const { contextData, isPatron } = useSelector(userSelector)
	const { data } = useGetActiveGroupsQuery({ refetch: contextData })
	if (!data) {
		return []
	}

	const docs = [...data.groups, ...(data.rootGroup ? [data.rootGroup] : [])]
	let members = docs.flatMap((d: any) => d?.members ?? [])

	if (isPatron) {
		members = members.concat(
			data.rootGroup?.memberPool?.map((o: any) => ({ profile: { _id: o._id, name: o.name }, hasGroupRoles: [] })) ?? []
		)
	}

	if (mergeRoles) {
		const withMergedRoles = _.values(_.groupBy(members, (m: any) => m.profile._id)).reduce((a: any, b: any) => {
			const arr = b
			if (arr.length === 1) {
				return a.concat(arr)
			} else {
				const hasGroupRoles = _.uniq(_.flatMap(arr, "hasGroupRoles"))
				return a.concat({ ...arr[0], hasGroupRoles })
			}
		}, [])
		members = withMergedRoles
	}

	members = _.uniqBy(members, (m: any) => m.profile?._id)
	members = members.filter((m: any) => m.profile)
	members.sort((a: any, b: any) => a.profile.name.localeCompare(b.profile.name))

	return members
}

/**
 * Returns current profile's accessible root groups, dependent on context.
 * This is primarily determined from being in `rootGroup.memberPool` array.
 */
export const useRootGroup = () => {
	// Add non-handled query argument to refetch on context data changes
	const { contextData } = useSelector(userSelector)
	const { data } = useGetActiveGroupsQuery({ refetch: contextData })
	if (!data) {
		return null
	}

	return data.rootGroup as any
}
/**
 * Returns current profile's specific group, dependent on context
 *
 * Supports admin context settings; will perform a query in that case.
 */
export const useCurGroup = () => {
	const { contextData } = useSelector(userSelector)
	const groups = useGroups()
	return groups.find((g: any) => (g?._id ?? g) === contextData.group)
}

const useCurGroupMembers = () => {
	const group = useCurGroup()
	if (!group) {
		return []
	}
	return group.members
}

export const useAllAvailableMembers = () => {
	// Add non-handled query argument to refetch on context data changes
	const { contextData } = useSelector(userSelector)
	const { data } = useGetActiveGroupsQuery({ refetch: contextData })
	if (!data) {
		return []
	}
	const memberPoolMembers = !data.rootGroup ? [] : data.rootGroup.memberPool
	const rootGroupMembers = !data.rootGroup ? [] : data.rootGroup.members.map((m: any) => m.profile)
	const groupMembers = data.groups.flatMap((g: any) => g.members.map((m: any) => m.profile))
	const members = _.uniqBy([...memberPoolMembers, ...rootGroupMembers, ...groupMembers], "name")

	return members
}

export const useCurGroupPlayers = () => {
	const members = useCurGroupMembers()
	const playerLikeRoles = usePlayerLikeRoleNames()
	return useMemo(() => {
		const playerMembers = members.filter((m: any) => m.hasGroupRoles.some((gr: any) => playerLikeRoles.includes(gr)))
		return playerMembers.map((m: any) => m.profile)
	}, [members, playerLikeRoles])
}

/**
 * Returns current profile's specific root group, dependent on context
 *
 * Supports admin context settings; will perform a query in that case.
 */
export const useCurRootGroup = () => {
	const rootGroup = useRootGroup()
	return rootGroup
}
/**
 * Returns the currently chosen role definition
 */
export const useRoleDefinition = () => {
	const { profileDataForCtx } = useSelector(userSelector)
	const roleDef = profileDataForCtx?.activeMemberRole
	if (!roleDef) {
		return {
			rolePermMap: {},
			itemPerms: [],
			generalPerms: [],
			name: "basic_user",
			root: "",
			inGroups: [],
			roleProfileIdMap: {},
		}
	}

	return roleDef
}

const traverseAndSet = (obj: any, attribute: string, value: any) => {
	if (typeof obj !== "object" || obj === null) {
		return
	}
	for (const k in obj) {
		if (k === attribute) {
			obj[k] = value
		}
		traverseAndSet(obj[k], attribute, value)
	}
}

/**
 * Returns the current operational class.
 *
 * Falls back to the current sport context, and then the first found
 * operational root group class.
 *
 * If null is returned from the hook, it means that queries
 * are loading. If the return value is undefined, it means that no operational
 * classes exist in the database.
 *
 * @param {String} [rgId]																										Optional parameter for retrieving a specific root group's operational class instead of from own context
 * @param {Object} opts																											Options
 * @param {Boolean} opts.localize																						Apply localization on RootGroupClass. Defaults to true.
 * @param {Boolean}	opts.ignoreDisplayNames																	Prevent fallback to default language page customization display names if there is not one defined for current language. Defaults to true
 * @returns {import("@myeway/types").RootGroupClassInterface}			RootGroupClass document of type "operational"
 */
export const useOperationalClass = (rgId?: any, opts: any = {}) => {
	let { localize, ignoreDisplayNames } = opts
	if (localize === undefined) {
		localize = true
	}
	if (ignoreDisplayNames === undefined) {
		ignoreDisplayNames = true
	}

	rgId = rgId?._id ?? rgId

	const { contextData } = useSelector(userSelector)

	const rootGroupId = rgId ?? contextData.rootGroup
	const { data: rootData, isLoading: rootLoading } = useGetRootGroupQueryL({ id: rootGroupId }, { skip: !rootGroupId })
	const { data: classData } = useGetRootGroupClassesQueryL(null, { skip: rootLoading || rootData })

	const opClass = useMemo(() => {
		if (!rootData && !classData) {
			return { eventTypes: [], personalEventTypes: [] }
		}

		let result

		if (rootData) {
			result = rootData?.classes?.find((c: any) => c.type === "operational")
		}

		if (!result) {
			result = classData?.find((c: any) => c.type === "operational" && c.name === "football")
		}

		if (localize) {
			result = locData(result, undefined)
		}

		if (ignoreDisplayNames) {
			traverseAndSet(result?.pageCustomizations, "displayName", "")
		}

		return result
	}, [rootData, classData, localize, ignoreDisplayNames])

	return opClass
}

/**
 * Returns dictionary keyed by RootGroupClass type. The values are retrieved from
 * `profileDataForCtx` redux state if the target is the current context root group,
 * endpoint queries otherwise.
 *
 * @param {String} rgId 													RootGroup id
 * @param {Object} opts														Options
 * @param {Boolean} opts.localize									Apply localization on RootGroupClass. Defaults to true.
 * @param {Boolean}	opts.ignoreDisplayNames				Prevent fallback to default language page customization display names if there is not one defined for current language. Defaults to true
 * @returns {Object}															Dictionary with keys 'operational', 'process' and 'productLvl'
 */
export const useRGCDic = (rgId?: any, opts: any = {}) => {
	let { localize, ignoreDisplayNames } = opts
	if (localize === undefined) {
		localize = true
	}
	if (ignoreDisplayNames === undefined) {
		ignoreDisplayNames = true
	}

	rgId = rgId?._id ?? rgId

	const { contextData, profileDataForCtx } = useSelector(userSelector)

	const rootGroupId = rgId ?? contextData.rootGroup
	const { data: rootData, isLoading: rootLoading } = useGetRootGroupQueryL({ id: rootGroupId }, { skip: !rootGroupId })
	const { data: classData } = useGetRootGroupClassesQueryL(null, { skip: rootLoading || rootData })

	if (contextData.rootGroup === rgId || !rgId) {
		return {
			operational: profileDataForCtx.operationalClass,
			process: profileDataForCtx.processClasses ?? [],
			productLvl: profileDataForCtx.levelClasses ?? [],
		}
	}

	if (!rootData && !classData) {
		return {
			operational: null,
			process: [],
			productLvl: [],
		}
	}

	let classes = []

	if (rootData) {
		classes = rootData.classes
	}

	if (_.isEmpty(classes)) {
		classes = [classData?.find((c: any) => c.type === "operational" && c.name === "football")]
	}

	if (localize) {
		classes = classes.map((c: any) => locData(c, undefined))
	}

	const grouped: Record<string, any> = _.groupBy(classes, "type")
	grouped["operational"] = grouped["operational"]?.[0] ?? null
	grouped["process"] = grouped["process"] ?? []
	grouped["productLvl"] = grouped["productLvl"] ?? []

	return grouped
}
/**
 * Returns a mapping of dot-separated event types to available
 * task definitions. Will propagate parent event types' tasks to children automatically.
 *
 * Will return null if needed data is not yet available.
 */
export const useEventTaskMap = () => {
	const { data: eventTypes } = useGetEventTypesQueryL()
	const { data: taskDefData } = useGetTaskDefinitionsQueryL()
	if (!eventTypes || !taskDefData) {
		return null
	}
	const obj = eventTypes.reduce((a: any, b: any) => {
		const item = b
		let itemTasks = item.tasks ?? []
		itemTasks = itemTasks.map((id: any) => taskDefData.find((d: any) => d._id === id)).filter((o: any) => o)
		_.set(a, item.name, itemTasks)
		for (const c of item.children) {
			let childTasks = c.tasks ?? []
			childTasks = childTasks.map((id: any) => taskDefData.find((d: any) => d._id === id)).filter((o: any) => o)
			_.set(a, [`${item.name}.${c.name}`], _.uniqBy([...itemTasks, ...childTasks], "name"))
		}
		return a
	}, {})

	return obj
}

/**
 * Returns group options filtered by given rootgroups and customergroups array.
 *
 * Useful for getting list of possible options as part of a filtering pipeline.
 *
 * @param {{ rootGroups: object[], customerGroups: object[]}}
 */
export const useGroupOptionsForCustomerGroupsAndRootGroups = ({ customerGroups, rootGroups }: any) => {
	const { data: groups, isLoading } = useAllGroups()

	const getGroupOptions = (groups: any[]) => {
		return groups.map((group: any) => {
			if (rootGroups?.length > 0 && !rootGroups?.some((r: any) => r?._id === group.root?._id)) {
				return undefined
			}

			if (customerGroups?.length > 0 && customerGroups.every((cg: any) => !isWithinCustomerGroup(group.root, cg))) {
				return undefined
			}

			return group
		})
	}

	return {
		isLoading,
		options: groups ? _.compact(getGroupOptions(groups)) : null,
	}
}

/**
 * Handles processing of video data based on pagination options. Is also responsible for actually fetching the videolibrary videos based on the pagination options.
 *
 * @param {{ isRootGroupView: boolean, customFilter: (video: object) => boolean }}
 * 	Settings object. `isRootGroupView` determines what query to dispatch.
 * 	`customFilter` is an optional filter function applied after other filters.
 *
 * @returns Object with important information such as whether query for videos is loading,
 * the actual data response from the query, the pagination opts and finally the setter of the pagination options.
 */
export const useVideoPagination = (
	settings: { isRootGroupView?: boolean; customFilter?: (video: any) => boolean } = {
		isRootGroupView: false,
		customFilter: undefined,
	}
) => {
	const { isRootGroupView } = settings
	const { contextData } = useSelector(userSelector)
	const [opts, setOpts] = useQueryParamStateMultiple({
		page: {
			getState: (value: any) => Number(value),
			getParam: (state: any) => state,
			defaultValue: 1,
		},
		limit: {
			getState: (value: any) => Number(value),
			getParam: (state: any) => state,
			defaultValue: 20,
		},
		search: {
			getState: (value: any) => value,
			getParam: (state: any) => state,
		},
		size: {
			getState: (value: any) => value,
			getParam: (state: any) => state,
		},
		rootGroupId: {
			getState: (value: any) => value,
			getParam: (state: any) => state,
		},
		year: {
			getState: (value: any) => value,
			getParam: (state: any) => state,
		},
		month: {
			getState: (value: any) => Number(value),
			getParam: (state: any) => state,
		},
		weeks: {
			getState: (value: any) => value.split(","),
			getParam: (state: any) => state.join(","),
		},
		categories: {
			getState: (value: any) => JSON.parse(value),
			getParam: (state: any) => JSON.stringify(state),
			defaultValue: [],
			dependOn: [contextData.rootGroup],
		},
		sex: {
			getState: (value: any) => value,
			getParam: (state: any) => state,
		},
		birthyear: {
			getState: (value: any) => value,
			getParam: (state: any) => state,
		},
		agegroup: {
			getState: (value: any) => value,
			getParam: (state: any) => state,
		},
	})

	const query: any = isRootGroupView ? useListRootGroupVideosQuery : useListGeneralVideosQuery
	const {
		data: pagRes,
		isLoading,
		isFetching,
	} = query(
		{
			id: contextData.rootGroup,
			query: {
				paginate: true,
				...opts,
				month: opts.month !== null && !isNaN(opts.month) ? opts.month + 1 : null, // month is stored as zero-index
				excludeClips: opts?.clipsOnly ? false : true,
			},
		},
		{ skip: isRootGroupView && !contextData.rootGroup }
	)

	/** Processes items by applying filtering  */
	const flattenVideos = () => {
		const data = _.cloneDeep(pagRes)

		if (pagRes?.docs) {
			let videos = _.cloneDeep(pagRes.docs)
			if (videos?.general && videos?.rootGroup) {
				const generalVids = pagRes?.docs?.general ?? []
				const rootGroupVids = pagRes?.docs?.rootGroup ?? []

				videos = generalVids.concat(rootGroupVids)
			}

			data.docs = videos
		}

		return data
	}

	return {
		data: flattenVideos(),
		isLoading,
		isFetching,
		opts,
		setOpts,
	}
}

/**
 * Returns all video categories user has access to. For admins, fetches all video categories across all operational classes.
 *
 * Ensures uniqueness.
 * @returns {string[]}
 */
export const useVideoCategories = () => {
	const { data: rootGroupClasses } = useGetRootGroupClassesQueryL(null)
	const { contextData, isPlainAdmin } = useSelector(userSelector)
	const op = useOperationalClass(contextData.rootGroup)

	if (!op) return []

	return _(rootGroupClasses)
		.filter((o: any) => (isPlainAdmin || o.name === op.name) && o.type === "operational")
		.map((o: any) => o?.videoCategories ?? [])
		.flattenDeep()
		.uniq()
		.value()
}

/**
 * Returns template role options for admin. Useful for situations where admin can select multiple template roles.
 * @returns
 */
export const useTemplateRoleOptionsAdmin = () => {
	const { data: templateRoles, isLoading } = useGetTemplateRolesQueryL(null)

	const getTemplateRoleOptions = () => {
		return _(templateRoles).uniqBy("_id").flatten().value()
	}

	return {
		isLoading,
		options: getTemplateRoleOptions(),
	}
}

const useGetTemplateRoleByName = () => {
	const { data: templateRoles } = useGetTemplateRolesQueryL(null)
	return (name: string) => templateRoles?.find((tr) => tr.name === name)
}

export const useGetTemplateRoleTitle = () => {
	const getTemplateRole = useGetTemplateRoleByName()
	return (name: string) => {
		const tr = getTemplateRole(name)
		return tr?.title ?? "unknown"
	}
}

/**
 * Returns rootgroups options filtered by given customergroups array.
 *
 * Useful for getting list of possible options as part of a filtering pipeline.
 *
 * @param {number[]} customerGroups - One or many customer group objects in array
 */
export const useRootGroupOptionsForCustomerGroups = (customerGroups?: any[]) => {
	const { data: rootGroupData, isLoading } = useSearchRootGroupsQuery({
		pagination: false,
		limited: 1,
	})

	const getRootGroupOptions = (rootGroups: any[]) => {
		return rootGroups.map((rG: any) => {
			if ((customerGroups?.length ?? 0) > 0) {
				if (customerGroups?.every((cg: any) => !isWithinCustomerGroup(rG, cg))) {
					return undefined
				}
			}

			return { _id: rG?._id, name: rG?.name }
		})
	}

	return {
		isLoading,
		options: rootGroupData?.docs ? _.compact(getRootGroupOptions(rootGroupData.docs)) : null,
	}
}

/**
 * Returns all groups under rootgroups fetched via `useSearchRootGroupsQuery`.
 *
 * Useful for admin-level functionality since effectively returns all possible groups
 *
 * @returns
 */
const useAllGroups = () => {
	const { data: rootGroupData, isLoading: isRootGroupsLoading } = useSearchRootGroupsQuery({
		pagination: false,
		limited: 1,
	})

	return {
		isLoading: isRootGroupsLoading,
		data: rootGroupData?.docs?.flatMap((o: any) => o.groups.map((g: any) => ({ ...g, groupHeader: o.name, root: o }))),
	}
}

/**
 * Returns list of members of current group, optionally filtered by role name
 * @param {String} role 					Role name
 * @returns {[import("@myeway/types").Member]}						Array of `group.members` path objects
 */
export const useMembersInGroup = (role?: string, groupId?: any) => {
	const { contextData, isAdmin } = useSelector(userSelector)
	const curGroupId = groupId ?? contextData.group
	const curGroupMembers = useCurGroupMembers()

	const { data: groupData, isLoading } = useGetGroupByIdQueryL({ id: curGroupId }, { skip: !isAdmin || !curGroupId })

	if (!curGroupId || isLoading) {
		return []
	}

	let members = []
	if (isAdmin) {
		const group = groupData
		members = group?.members ?? []
	} else {
		members = curGroupMembers ?? []
	}

	if (role) {
		members = members.filter((m: any) => m.profile && m.hasGroupRoles.includes(role))
	}

	return members
}

export const useAvailableMembers = ({
	groupRole = "player",
	selectedGroups = [],
	whitelist = [],
	blacklist = [],
}: {
	groupRole?: string
	selectedGroups?: any[]
	whitelist?: any[]
	blacklist?: any[]
}) => {
	const { data: currentGroups, isLoading, ...rest } = useGroupsWithStatus()

	if (isLoading) {
		return {
			availableMembers: [],
			isLoading,
			...rest,
		}
	}

	const groupSubset = currentGroups.filter((g: any) =>
		selectedGroups.length > 0 ? selectedGroups.some((sG: any) => g._id === sG._id) : true
	)
	const subsetMembers = groupSubset.flatMap((g: any) => g.members)

	let availableMembers = subsetMembers
		.filter((m: any) => m.profile && m.hasGroupRoles.includes(groupRole))
		.map((m: any) => ({ name: m.profile.name, id: m.profile._id }))
	if (whitelist.length > 0) {
		availableMembers = availableMembers.filter((o: any) => whitelist.includes(o.id))
	}
	if (blacklist.length > 0) {
		availableMembers = availableMembers.filter((o: any) => !blacklist.includes(o.id))
	}
	// Remove objects with undefined values; else causes error
	availableMembers = availableMembers.filter((a: any) => !!a.name && !!a.id)

	// Ensure no duplicates
	availableMembers = _.uniqBy(availableMembers, "id")

	// Sort by name
	availableMembers.sort((a: any, b: any) => a.name.localeCompare(b.name))

	return {
		availableMembers,
		isLoading,
		...rest,
	}
}
/**
 * Returns EventType documents from backend API and
 * allows listing EventType children at top level
 *
 * @param {Boolean} flat			If true, includes EventType.children items at array top level
 * @returns {import("@myeway/types").EventTypeInterface[]}				List of EventType documents
 */
export const useEventTypeDocs = (flat: boolean = false) => {
	const { data: data } = useGetEventTypesQueryL()
	if (!data) {
		return []
	}
	if (!flat) {
		return data
	}
	const nested = _.flatMap(data, "children")
	return data.concat(nested)
}

/**
 * Returns function that allows retrieving EventType document based on
 * `name` identifier
 *
 * @returns {Function}											Function which allows retrieval of EventType document
 */
export const useGetEventTypeDoc = () => {
	const allDocs = useEventTypeDocs()
	const getEventTypeDoc = (eventTypeName: string, parentEventName?: string) => {
		return (parentEventName ? (allDocs.find((d: any) => d.name === parentEventName)?.children ?? []) : allDocs).find(
			(d: any) => d.name === eventTypeName
		)
	}
	return getEventTypeDoc
}

/**
 * Variant of the above that has its return value be the document itself instead of a
 * function to retrieve it. This can be preferebla if handling a single event instead of a list.
 *
 * @param {String} eventTypeName				The name identifier of the event type
 * @param {String} parentEventType			If the event type is a child of a parent event type, this specifies the parent event types name identifier.
 * 																			This is used to distinguish identical event type names across parent-child levels.
 */
export const useEventTypeDoc = (eventTypeName: string, parentEventName?: string) => {
	const fn = useGetEventTypeDoc()
	return fn(eventTypeName, parentEventName)
}

/**
 * Returns function that allows retrieving EventType document title based on
 * `name` identifier
 *
 * @returns {Function}												Function for returnin EventType title
 */
export const useGetEventTypeTitle = () => {
	const getEventTypeDoc = useGetEventTypeDoc()
	return (eventTypeName: string, parentEventName?: string) => getEventTypeDoc(eventTypeName, parentEventName)?.title ?? ""
}

/**
 * Returns exercise event types inherent to operational class, with the option to
 * also include event types present in 'durationsbytype' response data.
 *
 * Return values may vary depending on the state of the event type list query.
 *
 * @param {Object} keyedData 						"durationsbytype" endpoint response's 'data' attribute
 * @returns {Object}										Object with keys `guidedEventTypes`, `ownEventTypes` and query hook return values
 */
export const useGetExerciseEventTypes = (keyedData: any) => {
	const { data: eventTypeData, ...rest } = useGetEventTypesQueryL(null, { skip: !keyedData })
	const exerciseEventTypeData = eventTypeData?.filter((et: any) => et.isExercise)

	const opClass = useOperationalClass()

	const allEventTypes = opClass.eventTypes.concat(opClass.personalEventTypes)
	const opClassExerciseTypes = allEventTypes.filter((et: any) => et.isExercise)

	const opClassGuidedEventTypes = opClassExerciseTypes.filter((et: any) => getExerciseMainCategory(et) === "guided")
	const opClassOwnEventTypes = opClassExerciseTypes.filter((et: any) => getExerciseMainCategory(et) === "own")

	// Event type documents from response data keys
	const eventTypesInResponse = useMemo(() => {
		if (!keyedData || !exerciseEventTypeData) {
			return []
		}
		const d = keyedData
		let names = []
		for (const y in d) {
			for (const wk in d[y]) {
				const keys = _.keys(d[y][wk])
				names.push(...keys)
			}
		}
		names = _.uniq(names)
		const eventTypes = exerciseEventTypeData.filter((et: any) => names.includes(et.name))
		return eventTypes
	}, [keyedData, exerciseEventTypeData])

	// The used event type collection will be union of op class base event types and foreign event types present in stat data response
	const responseGuidedEventTypes = eventTypesInResponse.filter((et: any) => getExerciseMainCategory(et) === "guided")
	const guidedEventTypes = _.unionBy(opClassGuidedEventTypes, responseGuidedEventTypes, (o: any) => o.name)

	const responseOwnEventTypes = eventTypesInResponse.filter((et: any) => getExerciseMainCategory(et) === "own")
	const ownEventTypes = _.unionBy(opClassOwnEventTypes, responseOwnEventTypes, (o: any) => o.name)

	return { guidedEventTypes, ownEventTypes, ...rest }
}

/** Generic helper hook for managing sorting more easily.
 *
 * Works on the premise of a "sort key", that is, any type of valid key for an object e.g. "name".
 *
 * Initially sort key can be null, this means no sorting is performed and `items` will be returned as the initial input items.
 *
 * Most important function here is `handleNewSort`; it is used for actually telling this hook to sort according to parameters you define.
 * It works best when used with a list of objects where each object can include appropriate keys (e.g. "sortKey" or "sortMethodInitial").
 *
 * Optionally you can set the direction initially with `newSortMethodInitial`.
 *
 * NOTE ON CUSTOM SORTER: `orderBy`'s custom sorter takes in only ONE item, not two.
 *
 */
export const useTableSorting = (initialItems: any, initialActiveSortKey: any = null, initialActiveSortDirection: any = null) => {
	const DEFAULT_SORT_DIRECTION = "asc"

	const handleNewSort = ({ newSortKey, newSortMethodInitial = null, customSortFn }: any) => {
		if (newSortKey !== activeSortKey) {
			setActiveSortkey(newSortKey)
		}

		const HAS_NEW_SORT_METHOD = !!newSortMethodInitial
		const NEW_SORT_KEY_IS_EQUAL_TO_CURRENT = newSortKey === activeSortKey

		if (!NEW_SORT_KEY_IS_EQUAL_TO_CURRENT && HAS_NEW_SORT_METHOD) {
			setActiveSortDirection(newSortMethodInitial)
		} else if (NEW_SORT_KEY_IS_EQUAL_TO_CURRENT) {
			setActiveSortDirection(activeSortDirection === "asc" ? "desc" : "asc")
		} else {
			setActiveSortDirection(DEFAULT_SORT_DIRECTION)
		}

		if (customSortFn) {
			setCustomSortFn(() => customSortFn)
		} else {
			setCustomSortFn(null)
		}
	}

	const [activeSortKey, setActiveSortkey] = useState(initialActiveSortKey)
	const [customSortFn, setCustomSortFn] = useState<any>()
	const [activeSortDirection, setActiveSortDirection] = useState(initialActiveSortDirection ?? DEFAULT_SORT_DIRECTION)

	const items = useMemo(() => {
		if (!activeSortKey || !initialItems) {
			return initialItems
		} else {
			// Without using a custom sorter, lodash does the best it can via dynamic comparison based on the field key
			const predicate = customSortFn ? [customSortFn] : activeSortKey
			return _.orderBy(initialItems, predicate, activeSortDirection)
		}
	}, [JSON.stringify(initialItems), activeSortKey, activeSortDirection])

	return { activeSortKey, items, handleNewSort, activeSortDirection }
}

/**
 * Saves state into query parameters. Useful for saving filters between reloads.
 * Usage:
 *
 *
 * ```
 * const [state, setState] = useQueryParamState({
 *  	// Convert the query param string into actual state value
 *		getState: (value) => profileIds.find(profile => profile.id == value),
 *		// Convert state into query param string
 *		getParam: (state) => state.id,
 *		// Default value when no value is found from query param. Defaults to `null`
 *		defaultValue: null,
 *		// This is the actual query param key e.g. ?queryParam=value
 *		key: "queryParam"
 *	})
 *
 * ```
 *
 * @param {object} options
 * @param {string} options.key This is the actual query param key e.g. ?queryParam=value
 * @param {function} options.getState Convert the query param string into actual state value
 * @param {function} options.getParam Convert state into query param string
 * @param {any} options.defaultValue Default value when no value is found from query param. Defaults to `null`
 * @returns Array - [state, setState]
 */
export const useQueryParamState = ({ key, getState, getParam, defaultValue, dependOn }: any) => {
	const setState = (newValue: any) => {
		setActualState(newValue)
		setSearchParams(
			(searchParams: any) => {
				if (newValue) {
					searchParams.set(key, getParam(newValue))
				} else {
					searchParams.delete(key)
				}
				return searchParams
			},
			{ replace: true }
		)
	}

	const getStateFromParam = () => {
		const { search } = window.location
		const searchParams = new URLSearchParams(search)
		const param = searchParams.get(key)
		if (!param) return defaultValue ?? null
		let result
		try {
			result = getState(param)
		} catch {
			searchParams.delete(key)
			result = defaultValue ?? null
		}
		return result
	}

	const [, setSearchParams] = useSearchParams()
	const [actualState, setActualState] = useState(getStateFromParam())

	useEffect(() => {
		if (!dependOn) return
		setActualState(getStateFromParam())
	}, dependOn ?? [])

	return [actualState, setState] as const
}

/**
 * Helper hook for storing dates as ISO in query parameters, with fallback date if the query parameter is invalid or missing
 * @param queryParamKey {string} param key in url
 * @param fallbackDate {Date} date to fall back to if query param is missing or invalid
 * @param transformParsedDate {(date: Date) => Date} Optional callback used to modify the parsed date value
 * @returns {Date}
 */
export const useQueryParamDate = ({ queryParamKey = "date", fallbackDate, transformParsedDate }: any) => {
	const [searchParams] = useSearchParams()

	return useMemo(() => {
		fallbackDate = fallbackDate || startOfDay(new Date())

		const getDateFromQueryParam = () => {
			const dateStr = searchParams.get(queryParamKey)
			if (!dateStr) {
				return
			}

			const parsed = parseISO(dateStr)

			const transform = transformParsedDate ? transformParsedDate : (date: any) => date

			return isValid(parsed) ? transform(parsed) : undefined
		}

		const date = getDateFromQueryParam() || fallbackDate
		const url = new URL(window.location.href)

		url.searchParams.set(queryParamKey, formatISO(date, { representation: "date" }))
		// History API is used to not trigger a re-render
		window.history.replaceState(null, "", url.toString())
		return date
	}, [])
}

/**
 * Variant of `useQueryParamState` that handles a "configuration" of multiple keys. Useful for splitting large state filter objects into granular query parameters (which is a best practice).
 * @param {{ getState: Function, getParam: Function, defaultValue: any, dependOn: any }} config
 * @returns Array - [state, setState]
 */
export const useQueryParamStateMultiple = (config: any) => {
	const setState = (newValue: any) => {
		/**
		 * Prevents consecutive hook calls from causing a redirect loop; `setSearchParameters` would be called with 'stale'
		 * destination paths because `DebouncedTextInput` would trigger a delayed change in search parameters. This would
		 * occur when quickly navigating between two pages that use the hook.
		 */
		if (window.location.pathname !== location.pathname) {
			return
		}
		const changes = changesBetweenObjects(newValue, actualState)
		setActualState((prev: any) => ({ ...prev, ...changes }))
		setSearchParams(
			(searchParams: any) => {
				for (const key in changes) {
					const value = config[key]?.getParam(changes?.[key])
					if (value !== undefined) {
						searchParams.set(key, value)
					} else {
						searchParams.delete(key)
					}
				}
				return searchParams
			},
			{ replace: true }
		)
	}

	const getStateFromParam = () => {
		const { search } = window.location
		const searchParams = new URLSearchParams(search)
		const result: Record<string, any> = {}
		for (const key in config) {
			const defaultValue = config[key]?.defaultValue
			const getState = config[key].getState

			let param: any = searchParams.get(key)
			if (!param) {
				result[key] = defaultValue ?? null
				continue
			}
			if (param === "true") param = true
			if (param === "false") param = false
			try {
				result[key] = getState(param)
			} catch {
				searchParams.delete(key)
			}
		}
		return result
	}

	const location = useLocation()
	const [, setSearchParams] = useSearchParams()
	const [actualState, setActualState] = useState(getStateFromParam())

	const dependedValues = useMemo(() => Object.values(config).map(({ dependOn }: any) => dependOn ?? []), [])

	useEffect(() => {
		if (!dependedValues) return
		setActualState(getStateFromParam())
	}, [JSON.stringify(dependedValues)])

	return [actualState, setState] as const
}

export const useVideoPlayerUrl = (videoId: any) => {
	const { data, isLoading } = useGetVideoPlayerUrlQuery(videoId)
	const videoPlayerUrl = data?.url
	const usesClipper = data?.usesClipper
	const canLink = data?.canLink

	return { usesClipper, videoPlayerUrl, canLink, isLoading }
}

/**
 * @returns {import("@myeway/types").TaskDefinitionInterface[]}
 */
export const useOpClassTaskDefs = () => {
	const { isPlainAdmin } = useSelector(userSelector)
	const { data: taskDefData } = useGetTaskDefinitionsQueryL()
	const opClass = useOperationalClass()

	if (!taskDefData) {
		return []
	}

	if (isPlainAdmin) {
		return taskDefData
	}

	if (!opClass) {
		return []
	}
	const taskDefsAvailable =
		opClass?.assignedTasks?.map((td: any) => taskDefData?.find((d: any) => d._id === (td?._id ?? td))).filter((o: any) => o) ?? []
	return taskDefsAvailable
}

/**
 * Returns function for updating analytics for a list of profile ids. The hook maintains
 * a state of the previous request's profile ids to prevent identical queries when there
 * has been no change in currently viewed profiles.
 *
 * @param {string} view														The associated analytics view/type
 * @returns {(profileIds: string[]) => void}			Function to record analytics data
 */
export const useIdListAnalytics = (view: string = "tests") => {
	const [curAnalyticIds, setCurAnalyticIds] = useState<any[]>([])
	const [analyticsEvent] = useSendAnalyticsEventMutation()

	const handleAnalytics = useCallback(
		(profileIds: any) => {
			if (_.isEqual(profileIds, curAnalyticIds) || !profileIds?.length) {
				return
			}
			analyticsEvent({ viewed: profileIds, view })
				.unwrap()
				.then(() => setCurAnalyticIds(profileIds))
		},
		[curAnalyticIds]
	)

	return (profileIds: any) => handleAnalytics(profileIds)
}
